import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { Plus, Users, Ticket, IndianRupee, Calendar as CalIcon, X, Loader2, Edit3, Trash2, ScanLine, ShieldCheck, TrendingUp, FileText, Copy, Sparkles, ArrowRight, Activity } from "lucide-react";
import PosterDownload from "@/components/PosterDownload";
import { toast } from "sonner";

const emptyForm = {
  title: "", description: "", banner: "", banner_portrait: "", category: "College Fest",
  location: "", city: "", date: "", time: "", end_time: "",
  end_date: "",
  registration_opens_at: "", registration_closes_at: "",
  timezone: "Asia/Kolkata",
  organizer_name: "", tags: "",
  ai_photo_gallery_url: "",
  ai_gallery_qr_path: "",
  enable_match_room: false, // Phase 1 item 12 — Hooked-style per-event Match Room
  accent_theme: "auto", // Iter 47 — pink | yellow | blue | purple | auto
  event_type: "ticketed",
  booking_mode: "eventom",
  external_booking_links: [],
  type: "event", // P2.3 — event | experience
  artists: [], // P2.5 — [{name, genre, photo}]
  maps_link: "", // P2.6
  location_name: "",
  tiers: [{ name: "Early Bird", price: 199, quantity: 100 }, { name: "Regular", price: 349, quantity: 500 }],
};

export default function OrganizerDashboard() {
  const { user, loading, refresh } = useAuth();
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [heatmapEvent, setHeatmapEvent] = useState(null); // Iter 47 — live heatmap modal
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("events"); // events | kyc | insights
  const [insightsEventId, setInsightsEventId] = useState("");
  const [insights, setInsights] = useState(null);
  const [kyc, setKyc] = useState({
    brand_name: "", gst_number: "", gst_doc_path: "",
    name_as_aadhaar: "", aadhaar_doc_path: "",
    bank_account_holder: "", bank_account_number: "", bank_ifsc: "",
  });
  const [kycSaving, setKycSaving] = useState(false);
  const [kycUploading, setKycUploading] = useState("");
  const [showBoost, setShowBoost] = useState(false);

  const load = async () => {
    if (!user) return;
    const [{ data: e }, { data: a }] = await Promise.all([
      api.get("/organizer/events"),
      api.get("/organizer/analytics"),
    ]);
    setEvents(e); setAnalytics(a);
    // Load KYC state from user
    setKyc((k) => ({
      brand_name: user.brand_name || "",
      gst_number: user.gst_number || "",
      gst_doc_path: user.gst_doc_path || "",
      name_as_aadhaar: user.name_as_aadhaar || "",
      aadhaar_doc_path: user.aadhaar_doc_path || "",
      bank_account_holder: user.bank_account_holder || "",
      bank_account_number: user.bank_account_number || "",
      bank_ifsc: user.bank_ifsc || "",
    }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const loadInsights = async (eventId) => {
    if (!eventId) return;
    setInsightsEventId(eventId);
    setInsights(null);
    try {
      const { data } = await api.get(`/organizer/events/${eventId}/attendees`);
      setInsights(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load insights");
    }
  };

  const uploadKycDoc = async (field, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
    const kind = field === "aadhaar_doc_path" ? "kyc-aadhaar" : "kyc-gst";
    const fd = new FormData();
    fd.append("file", file);
    setKycUploading(field);
    try {
      const { data } = await api.post(`/uploads?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setKyc((k) => ({ ...k, [field]: data.path }));
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setKycUploading("");
    }
  };

  const saveKyc = async (e) => {
    e.preventDefault();
    setKycSaving(true);
    try {
      await api.patch("/organizer/kyc", kyc);
      toast.success("KYC saved — admin will review shortly");
      await refresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setKycSaving(false);
    }
  };

  const orgCompletion = user?.organizer_completion ?? 0;
  const kycStatus = user?.kyc_status || "pending";

  if (loading) return <Layout><div className="p-10 font-display text-3xl uppercase">Loading...</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;
  if (!["organizer", "admin"].includes(user.role)) return <Navigate to="/" replace />;

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      ...emptyForm, ...e,
      tags: (e.tags || []).join(", "),
      tiers: e.tiers?.length ? e.tiers : emptyForm.tiers,
      artists: Array.isArray(e.artists) ? e.artists : [],
      date: (e.date || "").split("T")[0],
      end_date: (e.end_date || "").split("T")[0],
      type: e.type || "event",
    });
    setShowModal(true);
  };

  const submitEvent = async (ev) => {
    ev.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...form,
        tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags,
        tiers: form.event_type === "ticketed" ? form.tiers.filter((t) => t.name && t.price >= 0) : [],
        artists: (form.artists || []).filter((a) => a && a.name),
        end_date: form.end_date || null,
      };
      if (editingId) { await api.patch(`/events/${editingId}`, payload); toast.success("Event updated"); }
      else { await api.post("/events", payload); toast.success("Event created — pending approval"); }
      setShowModal(false); setEditingId(null); setForm(emptyForm); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const uploadInviteCsv = async (eventId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/events/${eventId}/invite-guests`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Invited ${data.invited} guest(s), skipped ${data.skipped}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "CSV upload failed");
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    try { await api.delete(`/events/${id}`); toast.success("Event deleted"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
  };

  const stats = [
    { label: "Total Events", value: analytics?.total_events ?? 0, icon: CalIcon, color: "from-magenta/30 to-magenta/10" },
    { label: "Registrations", value: analytics?.total_registrations ?? 0, icon: Users, color: "from-lime/25 to-lime/5" },
    { label: "Tickets Sold", value: analytics?.tickets_sold ?? 0, icon: Ticket, color: "from-orangex/25 to-orangex/5" },
    { label: "Net Payout (₹)", value: (analytics?.net_payout ?? analytics?.revenue ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 }), icon: IndianRupee, color: "from-purplex/30 to-purplex/10" },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-lime">Organiser Dashboard</div>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-[0.9]">Your events, <span className="grad-text-hot">live.</span></h1>
          </div>
          <button data-testid="org-new-event" onClick={openCreate} className="btn-primary"><Plus className="w-4 h-4" /> New Event</button>
          <Link to="/scanner" data-testid="org-scanner-link" className="btn-ghost !py-2 !px-4"><ScanLine className="w-4 h-4" /> Scan Tickets</Link>
        </div>

        {/* Primary organiser actions — 4 tiles: List / Boost / Biz / Consultant */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="org-primary-actions">
          <button onClick={openCreate} data-testid="org-cta-list-event"
            className="group relative rounded-3xl border border-lime/40 bg-gradient-to-br from-lime/25 to-lime/5 p-5 text-left transition-all hover:-translate-y-0.5 hover:border-lime overflow-hidden">
            <span className="pointer-events-none absolute -right-8 -top-8 w-32 h-32 rounded-full bg-lime/20 blur-3xl" />
            <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-lime text-ink mb-3"><Plus className="w-5 h-5" /></span>
            <div className="relative font-display text-2xl leading-none">Get Event <span className="grad-text-hot">Live.</span></div>
            <div className="relative text-[11px] font-mono uppercase tracking-widest text-lime mt-2 flex items-center gap-1">
              List in minutes <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>
          <button onClick={() => setShowBoost(true)} data-testid="org-cta-boost"
            className="group relative rounded-3xl border border-magenta/40 bg-gradient-to-br from-magenta/25 to-purplex/10 p-5 text-left transition-all hover:-translate-y-0.5 hover:border-magenta overflow-hidden">
            <span className="pointer-events-none absolute -right-8 -top-8 w-32 h-32 rounded-full bg-magenta/20 blur-3xl" />
            <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-magenta text-white mb-3"><Sparkles className="w-5 h-5" /></span>
            <div className="relative font-display text-2xl leading-none">Promote & <span className="grad-text-hot">Boost.</span></div>
            <div className="relative text-[11px] font-mono uppercase tracking-widest text-magenta mt-2 flex items-center gap-1">
              20K → 1.2 lakh reach <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>
          <Link to="/ecosystem" data-testid="org-cta-biz"
            className="group relative rounded-3xl border border-skyx/40 bg-gradient-to-br from-skyx/25 to-purplex/10 p-5 text-left transition-all hover:-translate-y-0.5 hover:border-skyx overflow-hidden">
            <span className="pointer-events-none absolute -right-8 -top-8 w-32 h-32 rounded-full bg-skyx/20 blur-3xl" />
            <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-skyx text-ink mb-3"><Users className="w-5 h-5" /></span>
            <div className="relative font-display text-2xl leading-none">Explore <span className="grad-text-hot">Biz.</span></div>
            <div className="relative text-[11px] font-mono uppercase tracking-widest text-skyx mt-2 flex items-center gap-1">
              DJs · Artists · Sponsors <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </Link>
          <a href="https://wa.me/917250120666?text=Hi%20Eventom%2C%20I'd%20like%20to%20talk%20to%20a%20consultant%20about%20my%20event." target="_blank" rel="noreferrer" data-testid="org-cta-consultant"
            className="group relative rounded-3xl border border-orangex/40 bg-gradient-to-br from-orangex/25 to-lime/5 p-5 text-left transition-all hover:-translate-y-0.5 hover:border-orangex overflow-hidden">
            <span className="pointer-events-none absolute -right-8 -top-8 w-32 h-32 rounded-full bg-orangex/20 blur-3xl" />
            <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-orangex text-white mb-3"><ArrowRight className="w-5 h-5" /></span>
            <div className="relative font-display text-2xl leading-none">Talk to a <span className="grad-text-hot">Consultant.</span></div>
            <div className="relative text-[11px] font-mono uppercase tracking-widest text-orangex mt-2 flex items-center gap-1">
              WhatsApp our team <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </a>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {stats.map((s) => (
            <div key={s.label} className={`bg-gradient-to-br ${s.color} border border-white/10 rounded-3xl p-5`}>
              <s.icon className="w-5 h-5 text-lime" />
              <div className="font-display text-4xl md:text-5xl leading-none mt-2 grad-text-hot">{s.value}</div>
              <div className="text-xs font-mono uppercase tracking-widest mt-1 text-muted">{s.label}</div>
            </div>
          ))}
        </div>
        {analytics && (analytics.gross_ticket_sales > 0 || analytics.commission_deducted > 0) && (
          <div className="glass rounded-2xl p-3 mb-6 text-[11px] font-mono uppercase tracking-widest text-muted flex flex-wrap gap-4" data-testid="org-analytics-detail">
            <span>Gross sales: <span className="text-white">₹{(analytics.gross_ticket_sales || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></span>
            <span>Commission (3.6%): <span className="text-magenta">− ₹{(analytics.commission_deducted || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></span>
            <span>Buyer platform fee collected: <span className="text-lime">₹{(analytics.platform_fee_collected || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></span>
          </div>
        )}

        {/* Phase 3: Tabs */}
        <div className="inline-flex gap-1 p-1 rounded-full bg-surface mb-6" data-testid="org-tabs">
          {[["events", "Events", CalIcon], ["kyc", "KYC & Bank", ShieldCheck], ["insights", "Insights", TrendingUp]].map(([k, l, Ic]) => (
            <button key={k} data-testid={`org-tab-${k}`} onClick={() => setTab(k)}
              className={`inline-flex items-center gap-2 px-4 py-1.5 text-xs font-mono uppercase tracking-widest rounded-full ${tab === k ? "bg-lime text-ink" : "text-muted hover:text-white"}`}>
              <Ic className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>

        {tab === "kyc" && (
          <div className="mb-10" data-testid="org-kyc-tab">
            <div className="glass rounded-3xl p-6 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-lime">Organiser KYC</div>
                  <div className="font-display text-2xl uppercase mt-1">Complete verification</div>
                </div>
                <span data-testid="org-kyc-status" className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full ${kycStatus === "approved" ? "bg-lime text-ink" : kycStatus === "rejected" ? "bg-magenta text-white" : "bg-orangex text-white"}`}>{kycStatus}</span>
              </div>
              <div className="max-w-md">
                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest mb-1">
                  <span className="text-muted">Completion</span>
                  <span className="text-lime font-bold" data-testid="org-kyc-completion">{orgCompletion}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-lime transition-all duration-500" style={{ width: `${orgCompletion}%` }} />
                </div>
              </div>
            </div>

            <form onSubmit={saveKyc} className="glass rounded-3xl p-6 space-y-4" data-testid="org-kyc-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input required data-testid="kyc-brand" placeholder="Brand / Company name" value={kyc.brand_name} onChange={(e) => setKyc({ ...kyc, brand_name: e.target.value })} className="input-dark" />
                <input data-testid="kyc-gst" placeholder="GST / MSME Number" value={kyc.gst_number} onChange={(e) => setKyc({ ...kyc, gst_number: e.target.value.toUpperCase() })} className="input-dark uppercase font-mono" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-muted">GST / MSME certificate (PDF or image)</label>
                <div className="mt-1 flex items-center gap-3">
                  <label className="btn-ghost !py-2 !px-3 !text-xs cursor-pointer" data-testid="kyc-gst-upload">
                    <FileText className="w-3.5 h-3.5" /> {kycUploading === "gst_doc_path" ? "Uploading..." : "Upload"}
                    <input type="file" accept=".pdf,image/*" hidden onChange={(ev) => uploadKycDoc("gst_doc_path", ev.target.files?.[0])} />
                  </label>
                  {kyc.gst_doc_path && <span className="text-[10px] font-mono text-lime truncate max-w-xs" data-testid="kyc-gst-name">✓ Uploaded</span>}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="kyc-aadhaar-name" placeholder="Name as per Aadhaar" value={kyc.name_as_aadhaar} onChange={(e) => setKyc({ ...kyc, name_as_aadhaar: e.target.value })} className="input-dark" />
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Aadhaar (PDF or image)</label>
                  <div className="mt-1 flex items-center gap-3">
                    <label className="btn-ghost !py-2 !px-3 !text-xs cursor-pointer" data-testid="kyc-aadhaar-upload">
                      <FileText className="w-3.5 h-3.5" /> {kycUploading === "aadhaar_doc_path" ? "Uploading..." : "Upload"}
                      <input type="file" accept=".pdf,image/*" hidden onChange={(ev) => uploadKycDoc("aadhaar_doc_path", ev.target.files?.[0])} />
                    </label>
                    {kyc.aadhaar_doc_path && <span className="text-[10px] font-mono text-lime" data-testid="kyc-aadhaar-name">✓ Uploaded</span>}
                  </div>
                </div>
              </div>

              <div className="font-mono text-[10px] uppercase tracking-widest text-lime pt-2">Bank Account (for payouts)</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="kyc-bank-holder" placeholder="Account holder name" value={kyc.bank_account_holder} onChange={(e) => setKyc({ ...kyc, bank_account_holder: e.target.value })} className="input-dark" />
                <input data-testid="kyc-bank-account" placeholder="Account number" value={kyc.bank_account_number} onChange={(e) => setKyc({ ...kyc, bank_account_number: e.target.value.replace(/\D/g, "") })} className="input-dark font-mono" inputMode="numeric" />
              </div>
              <input data-testid="kyc-bank-ifsc" placeholder="IFSC" value={kyc.bank_ifsc} onChange={(e) => setKyc({ ...kyc, bank_ifsc: e.target.value.toUpperCase() })} className="input-dark uppercase font-mono max-w-xs" />

              <button disabled={kycSaving} data-testid="kyc-save" className="btn-primary w-full justify-center">
                {kycSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {kycSaving ? "Saving..." : "Save & Submit for Review"}
              </button>
            </form>
          </div>
        )}

        {tab === "insights" && (
          <div className="mb-10" data-testid="org-insights-tab">
            <div className="glass rounded-3xl p-5 mb-4 flex items-start gap-3 border border-lime/30 bg-lime/5">
              <TrendingUp className="w-5 h-5 text-lime shrink-0 mt-0.5" />
              <div>
                <div className="font-display uppercase text-xl">Attendee Insights</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-lime mt-1" data-testid="biz-plan-banner">Included free with your organiser account</div>
                <p className="text-xs text-text/80 mt-2">Full attendee list per event: name, email, mobile, tier, entry status. Perfect for pre-event ops and post-event follow-ups.</p>
              </div>
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              <select data-testid="insights-event-select" value={insightsEventId} onChange={(e) => loadInsights(e.target.value)} className="input-dark !py-2 !px-3 !text-sm max-w-md">
                <option value="" className="bg-bg">— Pick an event —</option>
                {events.map((ev) => <option key={ev.id} value={ev.id} className="bg-bg">{ev.title}</option>)}
              </select>
            </div>
            {insights && (
              <div className="glass rounded-3xl overflow-hidden" data-testid="insights-table">
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="font-display text-xl uppercase leading-tight">{insights.event_title}</div>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-muted mt-1">{insights.total} attendee(s)</div>
                  </div>
                  <button data-testid="insights-copy-emails" onClick={() => { navigator.clipboard.writeText(insights.attendees.map((a) => a.email).filter(Boolean).join(", ")); toast.success("Emails copied"); }} className="btn-ghost !py-2 !px-3 !text-xs"><Copy className="w-3.5 h-3.5" /> Copy Emails</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="text-left p-3 font-mono uppercase tracking-widest text-[10px]">Name</th>
                        <th className="text-left p-3 font-mono uppercase tracking-widest text-[10px]">Email</th>
                        <th className="text-left p-3 font-mono uppercase tracking-widest text-[10px]">Mobile</th>
                        <th className="text-left p-3 font-mono uppercase tracking-widest text-[10px]">Tier</th>
                        <th className="text-left p-3 font-mono uppercase tracking-widest text-[10px]">Amount</th>
                        <th className="text-left p-3 font-mono uppercase tracking-widest text-[10px]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {insights.attendees.map((a, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="p-3">{a.name || "—"}</td>
                          <td className="p-3 font-mono text-[11px]">{a.email || "—"}</td>
                          <td className="p-3 font-mono text-[11px]">{a.mobile || "—"}</td>
                          <td className="p-3">{a.tier || "—"}</td>
                          <td className="p-3">₹{a.amount_paid || 0}</td>
                          <td className="p-3"><span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${a.entry_status === "entered" ? "bg-lime text-ink" : "bg-white/10 text-muted"}`}>{a.entry_status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "events" && (<>
        <h2 className="font-display text-2xl uppercase mb-4">Events</h2>
        {events.length === 0 ? (
          <div className="glass rounded-3xl p-6 font-mono text-sm text-muted">No events yet. Create your first one.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((e) => (
              <div key={e.id} className="glass rounded-3xl overflow-hidden card-lift" data-testid={`org-event-${e.id}`}>
                {e.banner && <div className="aspect-video overflow-hidden"><img src={e.banner} alt="" className="w-full h-full object-cover" /></div>}
                <div className="p-4">
                  <div className="font-display text-xl uppercase leading-tight">{e.title}</div>
                  <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
                    <span className={`px-2 py-0.5 rounded-full ${e.status === "approved" ? "bg-lime text-bg" : e.status === "pending" ? "bg-orangex text-white" : "bg-magenta text-white"}`}>{e.status}</span>
                    <span className="px-2 py-0.5 rounded-full bg-white/5 text-white">{e.category}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => openEdit(e)} data-testid={`org-edit-${e.id}`} className="btn-ghost !py-1.5 !px-3 !text-xs"><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                    <button onClick={() => deleteEvent(e.id)} data-testid={`org-delete-${e.id}`} className="btn-ghost !py-1.5 !px-3 !text-xs !text-magenta"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                    {e.event_type === "invite_only" && (
                      <label className="btn-ghost !py-1.5 !px-3 !text-xs !text-lime cursor-pointer" data-testid={`org-invite-csv-${e.id}`}>
                        <Users className="w-3.5 h-3.5" /> Upload Invite CSV
                        <input type="file" accept=".csv" hidden onChange={(ev) => { const f = ev.target.files?.[0]; if (f) uploadInviteCsv(e.id, f); ev.target.value = ""; }} />
                      </label>
                    )}
                    <PosterDownload event={e} testId={`org-poster-${e.id}`} label="Poster" />
                    <button onClick={() => setHeatmapEvent(e)} data-testid={`org-heatmap-${e.id}`} className="btn-ghost !py-1.5 !px-3 !text-xs !text-lime">
                      <Activity className="w-3.5 h-3.5" /> Live Heatmap
                    </button>
                    <a href={`${API}/events/${e.id}/poster.png`} target="_blank" rel="noreferrer"
                       download={`${(e.slug || e.id).slice(0, 20)}-ig-poster.png`}
                       className="btn-ghost !py-1.5 !px-3 !text-xs !text-lime"
                       data-testid={`org-ig-poster-${e.id}`}>
                      <Sparkles className="w-3.5 h-3.5" /> IG Poster
                    </a>
                    <OfflinePassButton event={e} testId={`org-passes-${e.id}`} />
                    {e.slug && (
                      <a href={`/${(e.type === "experience") ? "experiences" : "e"}/${e.slug}`} target="_blank" rel="noreferrer" data-testid={`org-view-${e.id}`} className="btn-ghost !py-1.5 !px-3 !text-xs">View →</a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>)}

        {showModal && (
          <div className="fixed inset-0 bg-bg/80 backdrop-blur-md z-[80] flex items-center justify-center p-4">
            <div className="max-w-2xl w-full glass rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="org-event-modal">
              <div className="flex justify-between items-center border-b border-white/10 p-4 sticky top-0 bg-bg/95 z-10">
                <div className="font-display text-2xl uppercase">{editingId ? "Edit Event" : "Create Event"}</div>
                <button onClick={() => setShowModal(false)} data-testid="org-event-close" className="icon-circle !w-9 !h-9 bg-white/5"><X className="w-4 h-4" /></button>
              </div>
              <form onSubmit={submitEvent} className="p-5 space-y-3">
                <input required data-testid="org-form-title" placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark" />
                <div className="relative">
                  <textarea rows={3} data-testid="org-form-desc" placeholder="Description — what's happening, who's playing, why they should come" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark resize-none pr-28" />
                  <button
                    type="button"
                    data-testid="org-form-ai-desc"
                    onClick={async () => {
                      if (!form.title) return toast.error("Add an event title first");
                      const t = toast.loading("Genie is drafting…");
                      try {
                        const prompt = `Event title: ${form.title}. Category: ${form.category || "event"}. City: ${form.city || "India"}. Format: ${form.type || "event"}. Tags: ${form.tags || "none"}. Existing notes: ${form.description || "none"}.`;
                        const { data } = await api.post("/genie/write", { prompt, kind: "announcement", tone: "editorial, Indian English, SEO-friendly" });
                        setForm((s) => ({ ...s, description: (data.reply || "").trim() }));
                        toast.success("SEO-friendly description drafted ✨", { id: t });
                      } catch (err) {
                        toast.error(formatApiError(err.response?.data?.detail) || "Genie failed", { id: t });
                      }
                    }}
                    className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-lime text-ink text-[10px] font-heading font-bold uppercase tracking-widest hover:scale-105 active:scale-95 transition-transform"
                  >
                    <Sparkles className="w-3 h-3" /> Write with Genie
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer" data-testid="org-form-banner-label">
                    <div className="w-24 h-24 rounded-2xl bg-white/5 border-2 border-dashed border-lime/40 overflow-hidden flex items-center justify-center">
                      {form.banner ? <img src={form.banner} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-mono text-muted text-center">Upload<br/>Banner</span>}
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; if (f.size > 3*1024*1024) return toast.error("Max 3MB"); const r = new FileReader(); r.onload = () => setForm({ ...form, banner: r.result }); r.readAsDataURL(f); }} className="hidden" data-testid="org-form-banner-input" />
                  </label>
                  <div className="text-[11px] text-muted font-mono flex-1 leading-relaxed">
                    <div className="text-lime uppercase tracking-widest text-[10px] mb-1">Event banner</div>
                    Recommended <span className="text-white">1600×900 px (16:9)</span> · JPG or PNG · max 3MB.
                    <div className="text-white/60 mt-1">Or paste a URL below.</div>
                  </div>
                </div>
                <input data-testid="org-form-banner-url" placeholder="…or paste banner URL" value={form.banner?.startsWith("data:") ? "" : (form.banner || "")} onChange={(e) => setForm({ ...form, banner: e.target.value })} className="input-dark !text-xs" />

                {/* Iter 49 — portrait cover for mobile hero + IG posters */}
                <div className="flex items-center gap-3" data-testid="org-form-banner-portrait-block">
                  <label className="cursor-pointer" data-testid="org-form-banner-portrait-label">
                    <div className="w-20 h-28 rounded-2xl bg-white/5 border-2 border-dashed border-pink/40 overflow-hidden flex items-center justify-center">
                      {form.banner_portrait ? <img src={form.banner_portrait} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] font-mono text-muted text-center">Upload<br/>Portrait</span>}
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; if (f.size > 5*1024*1024) return toast.error("Max 5MB"); const r = new FileReader(); r.onload = () => setForm({ ...form, banner_portrait: r.result }); r.readAsDataURL(f); }} className="hidden" data-testid="org-form-banner-portrait-input" />
                  </label>
                  <div className="text-[11px] text-muted font-mono flex-1 leading-relaxed">
                    <div className="text-pink uppercase tracking-widest text-[10px] mb-1">Portrait cover (optional)</div>
                    9:16 · minimum <span className="text-white">720×1280 px</span> · max 5MB. Used on mobile hero and IG stories.
                  </div>
                </div>
                <input data-testid="org-form-banner-portrait-url" placeholder="…or paste portrait URL" value={form.banner_portrait?.startsWith("data:") ? "" : (form.banner_portrait || "")} onChange={(e) => setForm({ ...form, banner_portrait: e.target.value })} className="input-dark !text-xs" />
                <div className="grid grid-cols-2 gap-3">
                  <input required data-testid="org-form-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark" />
                  <input data-testid="org-form-location" placeholder="Venue / Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-dark" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select data-testid="org-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-dark">
                    {["College Fest","Music Festival","Corporate Event","Technical Festival","Cultural","Fashion Show","Wedding","Open Mic","Other"].map((c) => <option key={c} className="bg-bg">{c}</option>)}
                  </select>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Start date</label>
                    <input required type="date" data-testid="org-form-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-dark !mt-1" />
                  </div>
                </div>
                {/* P2.2 Multi-day toggle + end-date picker */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="org-form-multiday-block">
                  <label className="flex items-start gap-2 cursor-pointer" data-testid="org-form-multiday-label">
                    <input type="checkbox" className="mt-1 accent-lime"
                      data-testid="org-form-multiday-toggle"
                      checked={!!form.end_date && form.end_date !== form.date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.checked ? (form.end_date || form.date || "") : "" })} />
                    <span>
                      <span className="text-[11px] font-mono uppercase tracking-widest text-lime">Runs across multiple days</span>
                      <span className="block text-[11px] text-white/70 mt-0.5">Same tiers and prices across every day — buyers pick which day they're attending.</span>
                    </span>
                  </label>
                  {!!form.end_date && form.end_date !== form.date && (
                    <div className="mt-3">
                      <label className="text-[10px] font-mono uppercase tracking-widest text-muted">End date</label>
                      <input type="date" data-testid="org-form-end-date" value={form.end_date || ""} min={form.date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input-dark !mt-1" />
                      {form.date && form.end_date && form.end_date >= form.date && (
                        <div className="mt-2 text-[11px] font-mono text-lime">
                          {(() => { try { const a=new Date(form.date), b=new Date(form.end_date); return Math.round((b-a)/86400000)+1; } catch { return "—"; } })()} days · same tiers across every day
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Format</label>
                  <div className="inline-flex gap-1 p-1 rounded-full bg-surface w-full mt-1" data-testid="org-form-type-toggle">
                    {[["event","Event"],["experience","Experience"]].map(([k,l]) => (
                      <button type="button" key={k} data-testid={`org-form-format-${k}`} onClick={() => setForm({ ...form, type: k })}
                        className={`flex-1 px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-full transition-colors ${form.type === k ? "bg-lime text-ink" : "text-muted"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Start time (IST)</label>
                    <input type="time" data-testid="org-form-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="input-dark !mt-1" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-muted">End time (IST)</label>
                    <input type="time" data-testid="org-form-end-time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="input-dark !mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Registration opens</label>
                    <input type="datetime-local" data-testid="org-form-reg-open" value={form.registration_opens_at} onChange={(e) => setForm({ ...form, registration_opens_at: e.target.value })} className="input-dark !mt-1 !text-xs" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Registration closes</label>
                    <input type="datetime-local" data-testid="org-form-reg-close" value={form.registration_closes_at} onChange={(e) => setForm({ ...form, registration_closes_at: e.target.value })} className="input-dark !mt-1 !text-xs" />
                  </div>
                </div>
                <input data-testid="org-form-tags" placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="input-dark" />

                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-lime">Google Maps Link (venue)</label>
                  <input type="url" data-testid="org-form-maps" placeholder="https://maps.google.com/... or https://goo.gl/maps/..." value={form.maps_link || ""} onChange={(e) => setForm({ ...form, maps_link: e.target.value })} className="input-dark !mt-1" />
                </div>

                <div>
                  <div className="font-mono text-xs uppercase tracking-widest mt-2 text-lime mb-2">Artists / Lineup (optional)</div>
                  {(form.artists || []).map((a, idx) => (
                    <div key={idx} className="grid grid-cols-[64px_1fr_1fr_auto] gap-2 mb-2 items-center">
                      <label className="cursor-pointer" data-testid={`org-artist-photo-label-${idx}`}>
                        <div className="w-14 h-14 rounded-xl bg-white/5 border-2 border-dashed border-lime/40 overflow-hidden flex items-center justify-center">
                          {a.photo ? <img src={a.photo} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] font-mono text-lime">+ Photo</span>}
                        </div>
                        <input type="file" accept="image/*" hidden data-testid={`org-artist-photo-upload-${idx}`} onChange={async (e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          if (f.size > 3 * 1024 * 1024) { toast.error("Max 3MB"); return; }
                          const fd = new FormData(); fd.append("file", f);
                          try {
                            const { data } = await api.post("/uploads?kind=artist-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                            const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
                            const arr = [...form.artists]; arr[idx] = { ...arr[idx], photo: url }; setForm({ ...form, artists: arr });
                            toast.success("Photo uploaded");
                          } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
                          e.target.value = "";
                        }} />
                      </label>
                      <input placeholder="Artist name" value={a.name || ""} onChange={(e) => { const arr = [...form.artists]; arr[idx] = { ...arr[idx], name: e.target.value }; setForm({ ...form, artists: arr }); }} className="input-dark !py-2 !text-sm" data-testid={`org-artist-name-${idx}`} />
                      <input placeholder="Genre / Role" value={a.genre || ""} onChange={(e) => { const arr = [...form.artists]; arr[idx] = { ...arr[idx], genre: e.target.value }; setForm({ ...form, artists: arr }); }} className="input-dark !py-2 !text-sm" data-testid={`org-artist-genre-${idx}`} />
                      <button type="button" onClick={() => setForm({ ...form, artists: form.artists.filter((_, i) => i !== idx) })} className="icon-circle !w-8 !h-8 bg-white/5 text-magenta"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <button type="button" data-testid="org-add-artist" onClick={() => setForm({ ...form, artists: [...(form.artists || []), { name: "", genre: "", photo: "" }] })} className="text-xs font-mono uppercase underline text-lime">+ Add artist</button>
                </div>

                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-lime">AI Photo Gallery Link (optional)</label>
                  <input type="url" data-testid="org-form-ai-gallery" placeholder="https://ai.eventom.online/…" value={form.ai_photo_gallery_url || ""} onChange={(e) => setForm({ ...form, ai_photo_gallery_url: e.target.value })} className="input-dark !mt-1" />
                  <div className="text-[10px] font-mono text-muted mt-1">Add the Eventom AI gallery link for this event. Attendees will be able to access their photos from the event page.</div>
                </div>

                <div className="rounded-2xl border border-magenta/30 bg-gradient-to-br from-magenta/10 to-purplex/5 p-3">
                  <label className="flex items-start gap-2 cursor-pointer" data-testid="org-form-match-room-label">
                    <input type="checkbox" checked={form.enable_match_room || false} onChange={(e) => setForm({ ...form, enable_match_room: e.target.checked })} className="mt-1 accent-magenta" data-testid="org-form-match-room" />
                    <span>
                      <span className="text-[11px] font-mono uppercase tracking-widest text-magenta">Enable Match Room</span>
                      <span className="block text-[11px] text-white/70 mt-0.5">Attendees can opt in with photo + interest 24h before doors. Room auto-wipes 24h after event ends. Everything stays inside this event.</span>
                    </span>
                  </label>
                </div>

                {/* Iter 47 — Themed Variant Picker: choose a poster accent for this event card */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="org-form-accent-block">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-lime">Poster Accent</div>
                  <div className="text-[11px] text-white/60 mt-0.5">Picks the colour used on the event card and detail page. Auto = rotates by position in the feed.</div>
                  <div className="mt-3 grid grid-cols-5 gap-2">
                    {[
                      { k: "auto",   label: "Auto",   dot: "bg-gradient-to-br from-pink via-[#FFC600] to-purple" },
                      { k: "pink",   label: "Pink",   dot: "bg-pink" },
                      { k: "yellow", label: "Yellow", dot: "bg-[#FFC600]" },
                      { k: "blue",   label: "Blue",   dot: "bg-skyx" },
                      { k: "purple", label: "Purple", dot: "bg-purple" },
                    ].map((s) => (
                      <button
                        type="button"
                        key={s.k}
                        data-testid={`org-form-accent-${s.k}`}
                        onClick={() => setForm({ ...form, accent_theme: s.k })}
                        className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-2 transition-all ${form.accent_theme === s.k ? "border-lime bg-lime/10" : "border-white/10 hover:border-white/30"}`}
                      >
                        <span className={`w-6 h-6 rounded-full ${s.dot} shadow`} />
                        <span className="text-[10px] font-mono uppercase tracking-widest text-white/80">{s.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-lime">Gallery QR Image (optional)</label>
                  <div className="mt-1 flex items-center gap-3">
                    <label className="btn-ghost !py-2 !px-3 !text-xs cursor-pointer" data-testid="org-form-qr-upload">
                      <FileText className="w-3.5 h-3.5" /> {form.ai_gallery_qr_path ? "Replace QR" : "Upload QR"}
                      <input type="file" accept="image/*" hidden onChange={async (ev) => {
                        const f = ev.target.files?.[0]; if (!f) return;
                        if (f.size > 3*1024*1024) return toast.error("Max 3MB");
                        const fd = new FormData(); fd.append("file", f);
                        try {
                          const { data } = await api.post("/uploads?kind=gallery-qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          setForm({ ...form, ai_gallery_qr_path: data.path });
                          toast.success("QR uploaded");
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
                      }} />
                    </label>
                    {form.ai_gallery_qr_path && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${form.ai_gallery_qr_path}`} alt="QR" className="w-16 h-16 bg-white p-1 rounded" data-testid="org-form-qr-preview" />}
                  </div>
                </div>

                <div className="font-mono text-xs uppercase tracking-widest mt-4 text-lime">Booking Mode</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["eventom","Sell via Eventom","We handle checkout"],["discovery_only","Discovery Only","Listed, no ticketing"],["external","External Links","BookMyShow / District etc."]].map(([val, label, sub]) => (
                    <button type="button" key={val} data-testid={`org-form-booking-${val}`}
                      onClick={() => setForm({ ...form, booking_mode: val })}
                      className={`text-left rounded-2xl border p-2 ${form.booking_mode === val ? "border-lime bg-lime/10" : "border-white/10 bg-white/5"}`}>
                      <div className="font-bold uppercase text-[11px] tracking-wider">{label}</div>
                      <div className="text-[9px] font-mono text-muted">{sub}</div>
                    </button>
                  ))}
                </div>
                {(form.booking_mode === "external" || form.booking_mode === "discovery_only") && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-lime mb-1">External Booking Links (optional)</div>
                    {(form.external_booking_links || []).map((l, idx) => (
                      <div key={idx} className="grid grid-cols-[130px_1fr_auto] gap-2 mb-2">
                        <input placeholder="Provider" value={l.provider} onChange={(e) => { const arr=[...form.external_booking_links]; arr[idx].provider=e.target.value; setForm({...form, external_booking_links: arr}); }} className="input-dark !py-2 !text-sm" />
                        <input type="url" placeholder="https://in.bookmyshow.com/..." value={l.url} onChange={(e) => { const arr=[...form.external_booking_links]; arr[idx].url=e.target.value; setForm({...form, external_booking_links: arr}); }} className="input-dark !py-2 !text-sm" />
                        <button type="button" onClick={() => setForm({...form, external_booking_links: form.external_booking_links.filter((_,i)=>i!==idx)})} className="icon-circle !w-8 !h-8 bg-white/5 text-magenta"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    <button type="button" data-testid="org-form-add-ext" onClick={() => setForm({...form, external_booking_links: [...(form.external_booking_links||[]), {provider:"BookMyShow", url:""}]})} className="text-xs font-mono uppercase underline text-lime">+ Add booking link</button>
                  </div>
                )}

                <div className="font-mono text-xs uppercase tracking-widest mt-4 text-lime">Event Type</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["ticketed","Ticketed","Sell paid tickets"],["free","Free RSVP","Free registration"],["invite_only","Invite Only","Manual guest list"]].map(([val, label, sub]) => (
                    <button type="button" key={val} data-testid={`org-form-type-${val}`}
                      onClick={() => setForm({ ...form, event_type: val })}
                      className={`text-left rounded-2xl border p-3 ${form.event_type === val ? "border-lime bg-lime/10" : "border-white/10 bg-white/5"}`}>
                      <div className="font-bold uppercase text-xs tracking-wider">{label}</div>
                      <div className="text-[10px] font-mono text-muted mt-0.5">{sub}</div>
                    </button>
                  ))}
                </div>

                {form.event_type === "ticketed" && (
                  <>
                    {/* Attach Biz partners you're using for this event — routes the org to
                        the Biz marketplace with their event context. Non-blocking. */}
                    <div className="rounded-2xl border border-skyx/30 bg-gradient-to-br from-skyx/10 to-purplex/5 p-3 mt-4 flex items-center justify-between gap-3" data-testid="org-attach-biz">
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-sm text-white leading-tight">Working with a DJ, artist or vendor?</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-skyx mt-1">Tag Biz partners to your event (optional)</div>
                      </div>
                      <Link to="/ecosystem" target="_blank" data-testid="org-attach-biz-cta" className="btn-ghost !py-2 !px-3 !text-[10px] shrink-0">
                        Open Biz →
                      </Link>
                    </div>
                    <div className="mt-4">
                      <div className="font-mono text-xs uppercase tracking-widest text-lime">Ticket Tiers</div>
                      <div className="text-[10px] font-mono text-muted mt-0.5">Category · Price · Qty · Perks (optional)</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-center px-1 text-[9px] font-mono uppercase tracking-widest text-muted hidden sm:grid">
                      <div className="col-span-3">Category</div>
                      <div className="col-span-2">Price ₹</div>
                      <div className="col-span-2">Qty</div>
                      <div className="col-span-4">Perks / notes</div>
                      <div className="col-span-1" />
                    </div>
                    {form.tiers.map((t, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`org-tier-${idx}`}>
                        <input data-testid={`org-tier-${idx}-name`} placeholder="e.g. Early Bird" value={t.name} onChange={(e) => { const arr = [...form.tiers]; arr[idx].name = e.target.value; setForm({ ...form, tiers: arr }); }} className="input-dark !py-2 !text-sm col-span-3" />
                        <input data-testid={`org-tier-${idx}-price`} type="number" placeholder="₹" value={t.price} onChange={(e) => { const arr = [...form.tiers]; arr[idx].price = (e.target.value === "" ? "" : Number(e.target.value)); setForm({ ...form, tiers: arr }); }} className="input-dark !py-2 !text-sm col-span-2" />
                        <input data-testid={`org-tier-${idx}-qty`} type="number" placeholder="100" value={t.quantity} onChange={(e) => { const arr = [...form.tiers]; arr[idx].quantity = (e.target.value === "" ? "" : Number(e.target.value)); setForm({ ...form, tiers: arr }); }} className="input-dark !py-2 !text-sm col-span-2" />
                        <input data-testid={`org-tier-${idx}-benefits`} placeholder="e.g. Free drink + priority entry" value={t.benefits || ""} onChange={(e) => { const arr = [...form.tiers]; arr[idx].benefits = e.target.value; setForm({ ...form, tiers: arr }); }} className="input-dark !py-2 !text-sm col-span-4" />
                        <button type="button" onClick={() => setForm({ ...form, tiers: form.tiers.filter((_, i) => i !== idx) })} data-testid={`org-remove-tier-${idx}`} className="icon-circle !w-8 !h-8 bg-white/5 text-magenta col-span-1"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setForm({ ...form, tiers: [...form.tiers, { name: "", price: 0, quantity: 0, benefits: "" }] })} data-testid="org-add-tier" className="text-xs font-mono uppercase underline text-lime">+ Add tier</button>
                  </>
                )}

                <button disabled={saving} data-testid="org-form-submit" type="submit" className="btn-primary w-full justify-center">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {saving ? "Saving..." : (editingId ? "Save Changes" : "Create Event")}
                </button>
              </form>
            </div>
          </div>
        )}

        {showBoost && <BoostPackageModal onClose={() => setShowBoost(false)} events={events} />}
        {heatmapEvent && <HeatmapModal event={heatmapEvent} onClose={() => setHeatmapEvent(null)} />}
      </div>
    </Layout>
  );
}

// Iter 47 — Live Event Heatmap modal
function HeatmapModal({ event, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);
  const [day, setDay] = useState("");  // P2.2 multi-day filter
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const q = day ? `&day=${encodeURIComponent(day)}` : "";
        const { data } = await api.get(`/organiser/events/${event.id}/heatmap?bucket_min=15${q}`);
        if (!cancel) setData(data);
      } catch (e) {
        if (!cancel) setErr(formatApiError(e.response?.data?.detail) || "Could not load heatmap");
      }
    };
    load();
    const t = setInterval(() => setTick((x) => x + 1), 10000);
    return () => { cancel = true; clearInterval(t); };
  }, [event.id, tick, day]);

  const totals = data?.totals;
  const curve = data?.arrival_curve || [];
  const peak = curve.reduce((m, p) => Math.max(m, p.scans), 0) || 1;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="org-heatmap-modal" onClick={onClose}>
      <div className="max-w-3xl w-full rounded-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="themed-blue sticker-wall p-6 relative">
          <button onClick={onClose} className="absolute top-4 right-4 icon-circle !w-9 !h-9 bg-black/30 text-white" data-testid="org-heatmap-close"><X className="w-4 h-4" /></button>
          <div className="relative z-10">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/80">Live heatmap · updates every 10s</div>
            <div className="font-fancy uppercase text-white leading-[0.9] mt-1 text-3xl md:text-5xl" style={{ textShadow: "3px 3px 0 rgba(0,0,0,0.3)" }}>
              {event.title}
            </div>
          </div>
        </div>
        <div className="bg-[#0A0A0F] p-6 space-y-5">
          {data?.is_multi_day && (
            <div className="flex flex-wrap items-center gap-2" data-testid="heatmap-day-picker">
              <span className="font-mono text-[10px] uppercase tracking-widest text-pink">Day</span>
              <button onClick={() => setDay("")} data-testid="heatmap-day-all"
                className={`px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest border ${!day ? "bg-lime text-ink border-lime" : "border-white/15 text-white/70"}`}>
                All days
              </button>
              {(data.day_dates || []).map((iso, i) => {
                const d = new Date(iso);
                return (
                  <button key={iso} onClick={() => setDay(iso)} data-testid={`heatmap-day-${iso}`}
                    className={`px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest border ${day === iso ? "bg-lime text-ink border-lime" : "border-white/15 text-white/70"}`}>
                    D{i+1} · {d.toLocaleDateString("en-IN",{weekday:"short", day:"2-digit"})}
                  </button>
                );
              })}
            </div>
          )}
          {err && <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{err}</div>}
          {!data && !err && <div className="text-sm text-white/60 font-mono">Loading…</div>}
          {totals && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Sold",      v: totals.sold,       c: "text-lime"    },
                  { label: "Scanned",   v: totals.scanned,    c: "text-skyx"    },
                  { label: "Remaining", v: totals.remaining,  c: "text-orangex" },
                  { label: "No-show",   v: `${totals.no_show_rate}%`, c: "text-magenta" },
                ].map((s) => (
                  <div key={s.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`heatmap-stat-${s.label.toLowerCase()}`}>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">{s.label}</div>
                    <div className={`font-fancy text-3xl ${s.c} mt-1`}>{s.v}</div>
                  </div>
                ))}
              </div>

              {data.tier_breakdown?.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-lime mb-3">Tier sell-through</div>
                  <div className="space-y-2.5">
                    {data.tier_breakdown.map((t) => (
                      <div key={t.name} data-testid={`heatmap-tier-${t.name}`}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-heading font-bold text-white">{t.name}</span>
                          <span className="font-mono text-white/70">{t.sold}/{t.capacity || "∞"} · {t.sell_through_pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-lime to-skyx" style={{ width: `${Math.min(100, t.sell_through_pct)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-pink mb-3">Arrival curve · 15-min buckets</div>
                {curve.length === 0 ? (
                  <div className="text-xs text-white/60 font-mono">No scans yet. Curve will populate once gates open.</div>
                ) : (
                  <div className="flex items-end gap-1 h-28">
                    {curve.slice(-24).map((p, i) => (
                      <div key={p.t + i} className="flex-1 bg-gradient-to-t from-pink to-magenta rounded-sm" title={`${p.t} · ${p.scans}`}
                           style={{ height: `${(p.scans / peak) * 100}%`, minHeight: 2 }} data-testid={`heatmap-bar-${i}`} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BoostPackageModal({ onClose, events }) {
  const [eventId, setEventId] = useState("");
  const PACKS = [
    { key: "20k",   price: 2500,  reach: "20K reach",   note: "Best for a single-city drop" },
    { key: "50k",   price: 5000,  reach: "50K reach",   note: "Multi-city push, top slots" },
    { key: "1.2L",  price: 10000, reach: "1.2 lakh reach", note: "Feature banner + city rail" },
    { key: "custom", price: null,  reach: "Custom",       note: "Multi-day, IP-branded campaign" },
  ];
  const wa = (p) => {
    const ev = events.find((e) => e.id === eventId);
    const evLine = ev ? `Event: ${ev.title} (${ev.city || ""})` : "Event: (pick on WhatsApp)";
    const msg = `Hi Eventom, I'd like to boost my event.\n${evLine}\nPackage: ${p.key === "custom" ? "Custom" : `₹${p.price.toLocaleString("en-IN")} · ${p.reach}`}\nPlease share next steps.`;
    return `https://wa.me/917250120666?text=${encodeURIComponent(msg)}`;
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="org-boost-modal">
      <div className="glass rounded-3xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="chip !text-magenta !border-magenta/40 mb-2">Promote & Boost</div>
            <div className="font-fancy text-3xl">Push your event to the crowd.</div>
            <div className="text-xs text-muted mt-1">Pick a pack. We'll take it forward on WhatsApp — no cart, no card upfront.</div>
          </div>
          <button onClick={onClose} className="btn-ghost !py-1.5 !px-2" data-testid="org-boost-close">✕</button>
        </div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-muted mb-1">Event to boost</label>
        <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="input-dark mb-4" data-testid="org-boost-event">
          <option value="">— pick one —</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.title} · {e.city || "—"}</option>)}
        </select>
        <div className="grid sm:grid-cols-2 gap-3">
          {PACKS.map((p) => (
            <a key={p.key} href={wa(p)} target="_blank" rel="noreferrer" data-testid={`org-boost-pack-${p.key}`}
              className="rounded-2xl border border-magenta/40 bg-gradient-to-br from-magenta/15 to-purplex/5 p-4 hover:border-magenta transition-colors block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-magenta mb-1">{p.reach}</div>
              <div className="font-display text-3xl leading-none">{p.price ? `₹${p.price.toLocaleString("en-IN")}` : "Custom"}</div>
              <div className="text-xs text-muted mt-1">{p.note}</div>
              <div className="inline-flex items-center gap-1 mt-3 text-[10px] font-mono uppercase tracking-widest text-white/90">Continue on WhatsApp →</div>
            </a>
          ))}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-4 text-center">Chat opens with your event + package pre-filled.</div>
      </div>
    </div>
  );
}


function OfflinePassButton({ event, testId }) {
  const [open, setOpen] = React.useState(false);
  const [tier, setTier] = React.useState((event.tiers?.[0]?.name) || "General");
  const [count, setCount] = React.useState(20);
  const [admits, setAdmits] = React.useState(1);
  const [busy, setBusy] = React.useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const url = `${API}/organizer/events/${event.id}/passes.pdf?tier=${encodeURIComponent(tier)}&count=${count}&admits=${admits}`;
      const token = localStorage.getItem("eventom_token");
      const res = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `eventom-${(event.slug || event.id).slice(0, 20)}-${tier.replace(/\s+/g, "-")}-${admits}x${count}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`${count} passes downloaded`);
      setOpen(false);
    } catch (e) {
      toast.error("Pass generation failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testId} className="btn-ghost !py-1.5 !px-3 !text-xs !text-skyx">
        <FileText className="w-3.5 h-3.5" /> Passes PDF
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid={`${testId}-modal`}>
          <div className="glass rounded-3xl max-w-md w-full p-6" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="chip !text-skyx !border-skyx/40 mb-1">Offline pass PDF</div>
                <div className="font-fancy text-2xl">Print-ready QR sheets.</div>
                <div className="text-xs text-muted mt-1">Each pass has a unique QR that scans just like an online ticket.</div>
              </div>
              <button onClick={() => setOpen(false)} className="btn-ghost !py-1 !px-2" data-testid={`${testId}-close`}>✕</button>
            </div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted mb-1">Ticket class</label>
            {event.tiers && event.tiers.length > 0 ? (
              <select value={tier} onChange={(e) => setTier(e.target.value)} className="input-dark mb-3" data-testid={`${testId}-tier`}>
                {event.tiers.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
            ) : (
              <input value={tier} onChange={(e) => setTier(e.target.value)} className="input-dark mb-3" data-testid={`${testId}-tier`} />
            )}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted mb-1">Passes to print</label>
                <input type="number" min={1} max={500} value={count} onChange={(e) => setCount(parseInt(e.target.value || "0", 10))} className="input-dark" data-testid={`${testId}-count`} />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted mb-1">Admits per pass</label>
                <select value={admits} onChange={(e) => setAdmits(parseInt(e.target.value, 10))} className="input-dark" data-testid={`${testId}-admits`}>
                  {[1, 2, 4, 6, 10].map((n) => <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>)}
                </select>
              </div>
            </div>
            <button onClick={download} disabled={busy} className="btn-primary w-full justify-center" data-testid={`${testId}-download`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Download {count} passes
            </button>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-3 text-center">
              6 passes per A4 page · Scan at the gate blocks duplicate use
            </div>
          </div>
        </div>
      )}
    </>
  );
}
